import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import { DateText } from "@/components/data";
import { isApiError } from "@/lib/api-error";
import { formatDateTime } from "@/lib/format";
import { toneSoftClasses } from "@/lib/tones";
import { cn } from "@/lib/utils";
import { whatsappApi } from "@/services/whatsapp-api";
import type { WhatsAppTranscriptMessage } from "@/types/whatsapp";

/** Espelha author_label da API (whatsapp/business/transcript.py). */
enum AutorDaMensagem {
  Contato = "CONTATO",
  Atendente = "ATENDENTE",
  IA = "IA",
  NotaInterna = "NOTA INTERNA",
}

const AUTORES = new Set<string>(Object.values(AutorDaMensagem));

function autorDaMensagem(autor: string): AutorDaMensagem | null {
  return AUTORES.has(autor) ? (autor as AutorDaMensagem) : null;
}

/** O limite do transcript na API: além disso, a conversa está cortada. */
const LIMITE_DO_TRANSCRIPT = 500;

const BALAO: Record<AutorDaMensagem, string> = {
  [AutorDaMensagem.Contato]: "mr-10 bg-muted",
  [AutorDaMensagem.Atendente]: cn("ml-10", toneSoftClasses.info),
  [AutorDaMensagem.IA]: cn("ml-10", toneSoftClasses.ai),
  [AutorDaMensagem.NotaInterna]: cn("mx-6 border-dashed", toneSoftClasses.warning),
};

const ROTULO: Record<AutorDaMensagem, string | null> = {
  [AutorDaMensagem.Contato]: null,
  [AutorDaMensagem.Atendente]: null,
  [AutorDaMensagem.IA]: "IA",
  [AutorDaMensagem.NotaInterna]: "Nota interna",
};

function Mensagem({ mensagem }: { mensagem: WhatsAppTranscriptMessage }) {
  const autor = autorDaMensagem(mensagem.autor);
  const rotulo = autor ? ROTULO[autor] : mensagem.autor;
  return (
    <div
      className={cn("rounded-lg border px-3 py-2 text-sm", autor ? BALAO[autor] : "bg-muted")}
      data-testid="mensagem-whatsapp"
      data-autor={mensagem.autor}
    >
      {rotulo && <p className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide opacity-70">{rotulo}</p>}
      <p className="whitespace-pre-wrap wrap-break-word">{mensagem.texto}</p>
      <DateText value={mensagem.created_at} kind="datetime" pattern="HH:mm" className="mt-0.5 block text-right text-[10px] text-muted-foreground" />
    </div>
  );
}

function Conversa({ conversationId }: { conversationId: string }) {
  const fim = useRef<HTMLDivElement | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ["whatsapp-transcript", conversationId],
    queryFn: () => whatsappApi.getTranscript(conversationId),
    staleTime: 60 * 1000,
  });

  useEffect(() => {
    if (data) fim.current?.scrollIntoView({ block: "end" });
  }, [data]);

  if (isLoading) return <Skeleton className="h-64 w-full" />;
  const mensagens = data?.messages ?? [];
  if (mensagens.length === 0) return <p className="text-sm text-muted-foreground">Nenhuma mensagem nesta conversa.</p>;

  let diaAnterior = "";
  return (
    <div className="space-y-2" data-testid="mensagens-conversa">
      {mensagens.length >= LIMITE_DO_TRANSCRIPT && (
        <p className="text-center text-xs text-muted-foreground">Mostrando as últimas {LIMITE_DO_TRANSCRIPT} mensagens.</p>
      )}
      <ol className="space-y-1.5">
        {mensagens.map((mensagem) => {
          const dia = formatDateTime(mensagem.created_at, "yyyy-MM-dd");
          const mudouDia = dia !== diaAnterior;
          diaAnterior = dia;
          return (
            <li key={mensagem.id} className="list-none space-y-1.5">
              {mudouDia && (
                <p className="pt-2 text-center text-xs text-muted-foreground">
                  <DateText value={mensagem.created_at} kind="datetime" pattern="EEEE, d 'de' MMMM 'de' yyyy" />
                </p>
              )}
              <Mensagem mensagem={mensagem} />
            </li>
          );
        })}
      </ol>
      <div ref={fim} />
    </div>
  );
}

/** Aba Mensagens do painel: a conversa de WhatsApp com o freelancer, como no espelho. */
export function CandidatoMensagensTab({ freelancerId }: { freelancerId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["whatsapp-resumo", freelancerId],
    queryFn: () => whatsappApi.resumoDoFreelancer(freelancerId),
    retry: false,
  });
  const conversas = data?.conversas ?? [];
  const [escolhida, setEscolhida] = useState<string | null>(null);
  const conversationId = conversas.find((conversa) => conversa.conversation_id === escolhida)?.conversation_id ?? conversas[0]?.conversation_id;

  if (isLoading) return <Skeleton className="h-64 w-full" />;
  if (isApiError(error) && error.status === 403) {
    return <p className="text-sm text-muted-foreground">Seu perfil não tem acesso às conversas do WhatsApp.</p>;
  }
  if (!conversationId) return <p className="text-sm text-muted-foreground">Sem conversa registrada.</p>;

  return (
    <div className="space-y-3">
      {conversas.length > 1 && (
        <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Conversas">
          {conversas.map((conversa) => (
            <button
              key={conversa.conversation_id}
              type="button"
              role="tab"
              aria-selected={conversa.conversation_id === conversationId}
              onClick={() => setEscolhida(conversa.conversation_id)}
              className={cn(
                "rounded-full border px-2.5 py-1 text-xs",
                conversa.conversation_id === conversationId ? "border-primary text-primary" : "text-muted-foreground",
              )}
            >
              {conversa.contact_name ?? conversa.telefone ?? "Conversa"}
            </button>
          ))}
        </div>
      )}
      <Conversa conversationId={conversationId} />
    </div>
  );
}
